// The feed (owner, 2026-10-10): grouped by client, in plain words —
// « Aida Tazi → aperçu validé » — with the agent's mistakes in red on the line.
// Filter: Tout · Erreurs · Passé à une personne. Five visible, then it scrolls.
import { useState } from 'react';
import { useT } from '@/lib/i18n';
import type { JournalGroup, MistakeFlag } from '@/lib/api';

export const FLAG_LABEL: Record<MistakeFlag, string> = {
  twice: 'envoyé 2×',
  price8: '8 Dh après le passage à 5',
  handoff2: 'passée 2× à une personne',
  nolink: 'promesse sans lien',
  blocked: 'message bloqué',
};

type Filter = 'all' | 'errors' | 'handoff';

export function LiveJournal({ groups, onSelect }: { groups: JournalGroup[]; onSelect: (g: JournalGroup) => void }) {
  const { t, lang } = useT();
  const [filter, setFilter] = useState<Filter>('all');
  const errors = groups.filter((g) => g.flags.length > 0);
  const handoffs = groups.filter((g) => g.handoff);
  const shown = filter === 'errors' ? errors : filter === 'handoff' ? handoffs : groups;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'fr-FR', {
    timeZone: 'Africa/Casablanca', hour: '2-digit', minute: '2-digit',
  });
  const tab = (key: Filter, label: string, n?: number) => (
    <button key={key} type="button" role="tab" aria-selected={filter === key} onClick={() => setFilter(key)}
      className={`px-3 min-h-[34px] text-[12px] cursor-pointer border-r last:border-r-0 border-border-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${filter === key ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300 font-semibold' : 'text-text-secondary hover:bg-bg-elevated'}`}>
      {label}{n != null && n > 0 ? <span className={`ml-1 tabular-nums ${key === 'errors' ? 'text-status-stuck font-semibold' : 'text-text-tertiary'}`}>{n}</span> : null}
    </button>
  );

  return (
    <section className="rounded-xl border border-border-subtle bg-bg-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border-subtle">
        <h3 className="font-heading text-[16px] text-text-primary flex-1">{t('Ce qu’a fait l’agent aujourd’hui')}</h3>
        <div role="tablist" aria-label={t('Filtre')} className="inline-flex rounded-lg border border-border-subtle overflow-hidden bg-bg-card">
          {tab('all', t('Tout'))}
          {tab('errors', t('Erreurs'), errors.length)}
          {tab('handoff', t('Passé à une personne'), handoffs.length)}
        </div>
      </div>
      {!shown.length && (
        <p className="px-4 py-5 text-[12.5px] text-text-tertiary">
          {filter === 'errors' ? t('Aucune erreur aujourd’hui.') : filter === 'handoff' ? t('Personne n’a été passée à une personne aujourd’hui.') : t('Rien aujourd’hui pour l’instant.')}
        </p>
      )}
      <ul className="max-h-[300px] overflow-y-auto divide-y divide-border-subtle">
        {shown.map((g) => {
          const [first, ...rest] = g.entries;
          return (
            <li key={`${g.chatId}-${g.lastAt}`}>
              <button type="button" onClick={() => onSelect(g)}
                className="w-full text-left px-4 py-2.5 grid grid-cols-[44px_minmax(0,1fr)] gap-3 cursor-pointer hover:bg-bg-elevated transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500">
                <span className="text-[11.5px] text-text-tertiary tabular-nums pt-px">{time(g.lastAt)}</span>
                <span className="min-w-0">
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <b className="text-[13px] font-semibold text-text-primary truncate max-w-[45%]">{g.name}</b>
                    <span className="text-[13px] text-text-secondary">→ {t(first?.text ?? '').toLowerCase()}</span>
                    {g.flags.map((f) => (
                      <span key={f} className="ml-auto rounded-full border border-status-stuck-border bg-status-stuck-bg px-2 py-px text-[11px] font-semibold text-status-stuck whitespace-nowrap">{t(FLAG_LABEL[f])}</span>
                    ))}
                  </span>
                  {rest.length > 0 && (
                    <span className="block text-[11.5px] text-text-tertiary truncate">
                      {rest.slice(0, 3).map((e) => `${time(e.at)} ${t(e.text).toLowerCase()}`).join(' · ')}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
